import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Wallet,
  CreditCard,
  Upload,
  Building2,
  DollarSign,
  AlertCircle,
  CheckCircle,
  X,
} from 'lucide-react';
import { useGetUserMarketplaceListingQuery, useGetMarketplacePaymentOptionsQuery, usePurchaseMarketplaceListingMutation } from '../../store/slices/apiSlice';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import toast from 'react-hot-toast';
import axios from 'axios';
import Navbar from '../../components/common/Navbar';

type PaymentMethod = 'wallet' | 'manual' | null;

const MarketplacePayment: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user, token } = useSelector((state: RootState) => state.auth);
  const listingId = id ? parseInt(id) : null;

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(null);
  const [paymentScreenshot, setPaymentScreenshot] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBankDetails, setShowBankDetails] = useState(false);

  const { data: listingResponse } = useGetUserMarketplaceListingQuery(listingId!, {
    skip: !listingId,
  });
  const { data: paymentOptionsResponse } = useGetMarketplacePaymentOptionsQuery(undefined, {
    skip: !user,
  });
  const [purchaseListing] = usePurchaseMarketplaceListingMutation();

  const listing = listingResponse?.data;
  const paymentOptions = paymentOptionsResponse?.data;
  const walletBalance = paymentOptions?.wallet_balance || 0;
  const listingPrice = listing?.price || 0;
  const minTransactionLimit = parseFloat(paymentOptions?.settings?.min_transaction_amount || '0');
  const maxTransactionLimit = parseFloat(paymentOptions?.settings?.max_transaction_amount || '0');
  const kycStatus = paymentOptions?.kyc_status || 'pending';
  const bankAccounts = paymentOptions?.bank_accounts || [];

  useEffect(() => {
    if (!user) {
      toast.error('Please login to make a payment');
      if (window.innerWidth < 1024) {
        window.location.href = "https://app.diasporawise.com";
      } else {
        navigate('/');
      }
      return;
    }

    // Only show transaction limit errors if we have valid data and are on the payment page
    // Don't show errors if listing price is 0 or if data is still loading
    if (listingPrice > 0 && maxTransactionLimit > 0) {
      // Check transaction limits - but don't show toast, just show the warning banner
      // The warning banner in the UI is sufficient, no need for toast on page load
    }
  }, [user, listingPrice, maxTransactionLimit, minTransactionLimit, kycStatus, navigate]);

  const formatCurrency = (amount: number, symbol: string | null = null) => {
    const currencySymbol = symbol || '$';
    return `${currencySymbol}${amount.toLocaleString('en-US')}`;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setPaymentScreenshot(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!paymentMethod) {
      toast.error('Please select a payment method');
      return;
    }

    // Validate transaction limits
    if (listingPrice > maxTransactionLimit && kycStatus !== 'approved') {
      toast.error('Transaction limit exceeded. Please complete KYC verification.');
      navigate('/marketplace/kyc');
      return;
    }

    if (listingPrice < minTransactionLimit) {
      toast.error(`Minimum transaction amount is ${formatCurrency(minTransactionLimit, listing?.currency_symbol)}`);
      return;
    }

    // Validate payment method availability
    if (paymentMethod === 'wallet' && !paymentOptions?.settings?.allow_wallet_payments) {
      toast.error('Wallet payments are currently disabled');
      return;
    }

    if (paymentMethod === 'manual' && !paymentOptions?.settings?.allow_bank_transfer) {
      toast.error('Bank transfer payments are currently disabled');
      return;
    }

    // Validate wallet balance if using wallet
    if (paymentMethod === 'wallet' && walletBalance < listingPrice) {
      toast.error('Insufficient wallet balance');
      return;
    }

    // Validate screenshot for manual payment
    if (paymentMethod === 'manual' && !paymentScreenshot) {
      toast.error('Please upload payment screenshot');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await purchaseListing({
        listing_id: listingId!,
        amount: listingPrice,
        payment_method: paymentMethod === 'wallet' ? 'wallet' : 'bank_transfer',
        wallet_amount: paymentMethod === 'wallet' ? listingPrice : undefined,
        payment_screenshot: paymentMethod === 'manual' && paymentScreenshot ? paymentScreenshot : undefined,
      }).unwrap();

      if (result?.success) {
        toast.success('Payment submitted successfully! Admin will verify your payment.');
        navigate('/marketplace/transactions');
      } else {
        toast.error(result?.message || 'Failed to submit payment');
      }
    } catch (error: any) {
      toast.error(error?.data?.message || error?.message || 'Failed to submit payment. Please try again.');
      console.error('Payment error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceedWithPayment = () => {
    if (listingPrice > maxTransactionLimit && kycStatus !== 'approved') {
      return false;
    }
    if (listingPrice < minTransactionLimit) {
      return false;
    }
    if (paymentMethod === 'wallet' && walletBalance < listingPrice) {
      return false;
    }
    return true;
  };

  if (!listing) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 py-8 px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="p-8 text-center">
              <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Listing Not Found</h2>
              <Button onClick={() => navigate('/marketplace')}>Back to Marketplace</Button>
            </Card>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(`/marketplace/${listingId}`)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Listing</span>
        </button>

        <Card className="p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Make Payment</h1>

          {/* Listing Summary */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h2 className="font-semibold text-gray-900 mb-2">{listing.title}</h2>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total Amount</span>
              <span className="text-2xl font-bold text-gray-900">
                {formatCurrency(listingPrice, listing.currency_symbol)}
              </span>
            </div>
          </div>

          {/* Transaction Limit Warnings */}
          {listingPrice > maxTransactionLimit && kycStatus !== 'approved' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-yellow-900 mb-1">Transaction Limit Exceeded</h3>
                  <p className="text-sm text-yellow-700 mb-3">
                    This transaction exceeds your limit of {formatCurrency(maxTransactionLimit, listing.currency_symbol)}.
                    Please complete KYC verification to increase your limit.
                  </p>
                  <Button
                    onClick={() => navigate('/marketplace/kyc')}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white"
                  >
                    Complete KYC Verification
                  </Button>
                </div>
              </div>
            </div>
          )}

          {listingPrice < minTransactionLimit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-red-900 mb-1">Amount Too Low</h3>
                  <p className="text-sm text-red-700">
                    Minimum transaction amount is {formatCurrency(minTransactionLimit, listing.currency_symbol)}.
                    This listing is below the minimum.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Payment Method Selection */}
          <form onSubmit={handlePaymentSubmit} className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Payment Method</h2>
              <div className="space-y-3">
                {/* Wallet Payment */}
                <button
                  type="button"
                  onClick={() => setPaymentMethod('wallet')}
                  disabled={walletBalance < listingPrice}
                  className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                    paymentMethod === 'wallet'
                      ? 'border-gray-800 bg-gray-50'
                      : 'border-gray-200 hover:border-gray-300'
                  } ${walletBalance < listingPrice ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Wallet className="w-6 h-6 text-gray-600" />
                      <div>
                        <div className="font-semibold text-gray-900">Wallet Balance</div>
                        <div className="text-sm text-gray-600">
                          Available: {formatCurrency(walletBalance, listing.currency_symbol)}
                        </div>
                      </div>
                    </div>
                    {paymentMethod === 'wallet' && <CheckCircle className="w-5 h-5 text-gray-800" />}
                  </div>
                  {walletBalance < listingPrice && (
                    <div className="mt-2 text-sm text-red-600">
                      Insufficient balance. Please add funds to your wallet.
                    </div>
                  )}
                </button>

                {/* Manual Payment */}
                <button
                  type="button"
                  onClick={() => setPaymentMethod('manual')}
                  className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                    paymentMethod === 'manual'
                      ? 'border-gray-800 bg-gray-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CreditCard className="w-6 h-6 text-gray-600" />
                      <div>
                        <div className="font-semibold text-gray-900">Manual Bank Transfer</div>
                        <div className="text-sm text-gray-600">Upload payment screenshot</div>
                      </div>
                    </div>
                    {paymentMethod === 'manual' && <CheckCircle className="w-5 h-5 text-gray-800" />}
                  </div>
                </button>
              </div>
            </div>

            {/* Manual Payment Details */}
            {paymentMethod === 'manual' && (
              <div className="space-y-4">
                <div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowBankDetails(!showBankDetails)}
                    className="w-full"
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    {showBankDetails ? 'Hide' : 'View'} Bank Account Details
                  </Button>
                  {showBankDetails && bankAccounts.length > 0 && (
                    <Card className="mt-4 p-4 bg-gray-50">
                      <h3 className="font-semibold text-gray-900 mb-3">Bank Account Details</h3>
                      <div className="space-y-4">
                        {bankAccounts.map((account) => (
                          <div key={account.bank_account_id} className="space-y-2 text-sm border-b border-gray-200 pb-3 last:border-0 last:pb-0">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Account Holder:</span>
                              <span className="text-gray-900">{account.account_holder_name}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Bank Name:</span>
                              <span className="text-gray-900">{account.bank_name}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Account Number:</span>
                              <span className="text-gray-900 font-mono">{account.account_number}</span>
                            </div>
                            {account.country_code && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Country:</span>
                                <span className="text-gray-900">{account.country_code}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Payment Screenshot *
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                      id="payment-screenshot"
                    />
                    <label
                      htmlFor="payment-screenshot"
                      className="cursor-pointer flex flex-col items-center"
                    >
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600 mb-1">
                        Click to upload or drag and drop
                      </span>
                      <span className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</span>
                    </label>
                  </div>
                  {previewImage && (
                    <div className="mt-4 relative">
                      <img
                        src={previewImage}
                        alt="Payment screenshot preview"
                        className="w-full h-48 object-contain border border-gray-300 rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setPaymentScreenshot(null);
                          setPreviewImage(null);
                        }}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/marketplace/${listingId}`)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!canProceedWithPayment() || isSubmitting || !paymentMethod}
                className="flex-1 bg-gray-800 hover:bg-gray-900"
              >
                {isSubmitting ? 'Processing...' : 'Submit Payment'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
      </div>
    </>
  );
};

export default MarketplacePayment;
